import * as SwitchPrimitive from "@radix-ui/react-switch";
import { forwardRef, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export interface SwitchProps
  extends Omit<React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>, "asChild"> {
  label?: ReactNode;
  description?: ReactNode;
}

/** Reemplazo de `<Switch>` de Mantine (Radix UI). */
export const Switch = forwardRef<React.ElementRef<typeof SwitchPrimitive.Root>, SwitchProps>(
  ({ className, label, description, id, ...props }, ref) => {
    const autoId = id ?? props.name;
    const track = (
      <SwitchPrimitive.Root
        ref={ref}
        id={autoId}
        className={cn(
          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full bg-ink-200 transition-colors dark:bg-ink-700",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-multiva-600/30",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          "data-[state=checked]:bg-multiva-600",
          className
        )}
        {...props}
      >
        <SwitchPrimitive.Thumb
          className={cn(
            "block size-4 translate-x-0.5 rounded-full bg-white shadow-sm transition-transform",
            "data-[state=checked]:translate-x-[18px]"
          )}
        />
      </SwitchPrimitive.Root>
    );
    if (!label && !description) return track;
    return (
      <div className="flex items-start gap-2">
        {track}
        <div className="flex flex-col gap-0.5">
          {label && (
            <label htmlFor={autoId} className="cursor-pointer text-sm text-foreground">
              {label}
            </label>
          )}
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
      </div>
    );
  }
);
Switch.displayName = "Switch";
